// src/context/TranslationsContext.tsx
"use client";

import Cookies from "js-cookie";
import React, { ReactNode, useCallback, useEffect, useState } from "react";
import { createContext, useContextSelector } from "use-context-selector";
import { registerCountryLocale } from "@/lib/countryTranslations";
import { Language } from "@/translations/supportedLanguages";
import { loadTranslations } from "@/utils/loadTranslations";
import { logger } from "@/utils/logger";

export type Direction = "ltr" | "rtl";

interface Store {
  t: (
    key: string,
    params?: Record<string, string | number>,
    fallback?: string
  ) => string;
  setLanguage: (lang: Language) => void;
  language: Language;
  direction: Direction;
}

const TransCtx = createContext<Store>({} as Store);

interface ProviderProps {
  children: ReactNode;
  initialLanguage: Language;
  messages?: Record<string, string>;
}

export const TranslationsProvider: React.FC<ProviderProps> = ({
  children,
  initialLanguage,
  messages: initialMessages = {},
}) => {
  const [language, setLanguage] = useState<Language>(initialLanguage);
  const [messages, setMessages] = useState<Record<string, string>>(initialMessages);

  // Persist language choice and register locale
  useEffect(() => {
    try {
      Cookies.set("language", language, { expires: 365 });
    } catch {
      // Ignore cookie failures
    }

    (async () => {
      try {
        await registerCountryLocale(language);
      } catch (err) {
        logger.error("Failed loading country locale", { err });
      }
    })();
  }, [language]);

  const loadLanguage = useCallback(
    async (lang: Language) => {
      if (lang === language) return;
      try {
        const msgs = await loadTranslations(lang);
        setMessages(msgs);
        setLanguage(lang);
      } catch (err) {
        logger.error("Failed to load translations JSON", { err });
      }
    },
    [language]
  );

  const lookup = useCallback(
    (key: string): string | undefined => {
      if (messages[key] !== undefined) return messages[key];
      return key
        .split(".")
        .reduce<any>(
          (o, seg) => (o && typeof o === "object" ? o[seg] : undefined),
          messages
        );
    },
    [messages]
  );

  const t: Store["t"] = useCallback(
    (key, params, fallback) => {
      let str = lookup(key) ?? fallback ?? key;
      if (params) {
        for (const [p, v] of Object.entries(params)) {
          const val = String(v);
          str = str.replaceAll(new RegExp(`{{\\s*${p}\\s*}}`, "g"), val);
        }
      }
      return str;
    },
    [lookup]
  );

  const store: Store = {
    language,
    direction: language === "ar" ? "rtl" : "ltr",
    t,
    setLanguage: loadLanguage,
  };

  return <TransCtx.Provider value={store}>{children}</TransCtx.Provider>;
};

export const useTranslationsSelector = <T,>(sel: (s: Store) => T) =>
  useContextSelector(TransCtx, sel);

export const useLanguage = () => useTranslationsSelector((s) => s.language);
export const useDirection = () => useTranslationsSelector((s) => s.direction);
export const useT = () => useTranslationsSelector((s) => s.t);
export const useSetLanguage = () =>
  useTranslationsSelector((s) => s.setLanguage);

export const useTranslations = (): Store =>
  useContextSelector(TransCtx, (s) => s);
